import { Modal, ModalProps } from 'components/page/app-page/Modal';
import React from 'react';
import tw from 'twin.macro';

import {
  WIZARD_PANEL_HEIGHT,
  WIZARD_PANEL_MAX_WIDTH,
} from 'components/video-gen/wizardLayout';

interface IVideoPreviewModal extends ModalProps {
  /**
   * SYM-6180. Size the dialog to what it is carrying.
   *
   * The wizard is as tall as the window allows (SYM-6288; it used to be "at
   * least 600px") and as wide as its Preview step needs (SYM-6308; it used to
   * be 1200px, wide enough for a video beside a working pane on every step).
   * A refusal is one sentence, and it was being shown in that
   * same dialog: a mostly empty box the size of the video editor holding one
   * line, which reads as something having gone wrong with the product rather
   * than as a normal answer with a normal cause.
   */
  compact?: boolean;
}

/**
 * A maximum width the shared Modal cannot take away.
 *
 * The shared Modal's panel carries `md:max-w-none`: from 768px up it removes
 * every maximum width. That rule sits in a media query, which is written out
 * after the plain declarations a caller passes in, so a plain `max-width` from
 * here loses on every screen wider than a phone and the dialog is simply 90%
 * of the window. Measured on round 8, 23 Sep 2026: 1296px in a 1440px window,
 * where SYM-6308 set 880px; the one-line refusal was as wide. Saying it again
 * inside the same media query puts it after the Modal's, so it wins there too.
 */
export const maxWidthThatHolds = (maxWidth: string) => ({
  maxWidth,
  '@media (min-width: 768px)': { maxWidth },
});

const VideoPreviewModal: React.FC<IVideoPreviewModal> = ({
  show,
  children,
  onClose,
  compact = false,
}) => {
  return (
    <Modal
      show={show}
      hideCloseButton={false}
      onClose={onClose}
      twclass={
        compact
          ? { ...tw`rounded-lg w-[90vw]`, ...maxWidthThatHolds('460px') }
          : {
              ...tw`rounded-lg w-[90vw]`,
              // SYM-6308 (22 Sep 2026). This was max-w-[1200px], a width the
              // pop-up needed while a playing video sat beside the working pane
              // on every step. The video is now only on the Preview step, so
              // the width is picked from that step: the video's own 384px cap
              // plus its pane's padding, over the share of the pop-up that pane
              // takes. The arithmetic and what it was measured against are in
              // wizardLayout.ts.
              ...maxWidthThatHolds(WIZARD_PANEL_MAX_WIDTH),
              // SYM-6288 (20 Sep 2026). This was "at least 600px, at most 85%
              // of the window, as tall as the content". The content is a 9:16
              // preview, so on a laptop the dialog hit the cap and scrolled as a
              // whole, taking the steps row or the button row off screen with
              // it. It now has a height of its own, taken from the window, and
              // the wizard lays itself out inside that (wizardLayout.ts). The
              // two lines after it switch off the shared Modal's 80vh cap and
              // the old 600px floor, either of which would overrule the height.
              height: WIZARD_PANEL_HEIGHT,
              maxHeight: 'none',
              minHeight: '0',
            }
      }
    >
      {children}
    </Modal>
  );
};

export default VideoPreviewModal;
